import React, { useEffect, useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Spin,
  message,
  Table,
  Tag,
  Switch,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import assetService, { type AssetDto, type AssetMovementDto } from '@/services/asset.service';
import assetGroupService from '@/services/asset-group.service';
import pmvService from '@/services/pmv.service';
import { RelatedMocsPanel } from '@/components/moc/RelatedMocsPanel';
import { AssetFleetMembershipCard } from '@/components/asset/AssetFleetMembershipCard';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';
import { IDENTIFICATION_METHOD_OPTIONS, tmmdeStatusColor, tmmdeStatusLabel } from '@/pages/tmmde/tmmdeDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ASSETS_QK = ['assets'];

function unwrapArray<T>(res: unknown): T[] {
  const ax = (res as { data?: { data?: T[]; success?: boolean } })?.data;
  const list = Array.isArray(ax) ? ax : Array.isArray(ax?.data) ? (ax as { data: T[] }).data : [];
  return list;
}

function mapDtoToForm(a: AssetDto): Record<string, unknown> {
  return {
    ...a,
    purchase_date: a.purchase_date ? dayjs(a.purchase_date) : null,
    capitalization_date: a.capitalization_date ? dayjs(a.capitalization_date) : null,
    warranty_expiry: a.warranty_expiry ? dayjs(a.warranty_expiry) : null,
  };
}

function formatDate(d: dayjs.Dayjs | null | undefined): string | null {
  if (!d || !d.isValid()) return null;
  return d.format('YYYY-MM-DD');
}

const AssetFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const user = useAuthStore((s) => s.user);
  const canRecordMovement = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.move'));
  const canEditAsset = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.edit'));
  const canManagePmv = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.profile.manage') ||
      user?.permissions?.includes('pmv.admin') ||
      user?.permissions?.includes('pmv.manage')
  );
  const qrForPreview = Form.useWatch('qr_code', form) as string | null | undefined;
  const isMeasuring = Boolean(Form.useWatch('is_measuring_equipment', form));
  const isEdit = Boolean(id);
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const { data: existing, isLoading: loadingRow } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => assetService.getById(id!),
    enabled: isEdit,
  });

  const { data: assetGroups = [] } = useQuery({
    queryKey: ['asset-groups'],
    queryFn: () => assetGroupService.list(false),
  });

  const { data: movements = [] } = useQuery({
    queryKey: ['asset', id, 'movements'],
    queryFn: () => assetService.listMovements(id!),
    enabled: isEdit,
  });

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses-asset-form'],
    queryFn: async () => {
      try {
        const res = await api.get('/inventory/warehouses');
        return unwrapArray<{ id: string; warehouse_code: string; warehouse_name: string }>(res);
      } catch {
        return [];
      }
    },
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments-asset-form'],
    queryFn: async () => {
      try {
        const res = await api.get('/finance/departments');
        return unwrapArray<{ id: string; department_code: string; department_name: string }>(res);
      } catch {
        return [];
      }
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-asset-form'],
    queryFn: async () => {
      try {
        const res = await api.get('/hr/employees');
        return unwrapArray<{ id: string; full_name: string; employee_code?: string }>(res);
      } catch {
        return [];
      }
    },
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-asset-form'],
    queryFn: async () => {
      try {
        const res = await api.get('/procurement/suppliers');
        return unwrapArray<{ id: string; supplier_name: string; supplier_code: string }>(res);
      } catch {
        return [];
      }
    },
  });

  const { data: pmvProfile, isFetched: pmvProfileLoaded, isLoading: pmvProfileLoading } = useQuery({
    queryKey: ['pmv-profile-by-asset', id],
    queryFn: () => pmvService.getProfileByAsset(id!),
    enabled: isEdit && Boolean(id) && canManagePmv,
    retry: false,
  });

  const { data: pmvWorkspace } = useQuery({
    queryKey: ['pmv-workspace-asset', pmvProfile?.id],
    queryFn: () => pmvService.getWorkspace(pmvProfile!.id),
    enabled: Boolean(pmvProfile?.id),
  });

  useEffect(() => {
    if (existing) {
      form.setFieldsValue(mapDtoToForm(existing));
    }
  }, [existing, form]);

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => assetService.create(body),
    onSuccess: () => {
      message.success('Asset created');
      void queryClient.invalidateQueries({ queryKey: ASSETS_QK });
      navigate('/asset/assets');
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: ({ i, body }: { i: string; body: Record<string, unknown> }) => assetService.update(i, body),
    onSuccess: () => {
      message.success('Asset updated');
      void queryClient.invalidateQueries({ queryKey: ASSETS_QK });
      void queryClient.invalidateQueries({ queryKey: ['asset', id] });
      navigate('/asset/assets');
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const genBarMut = useMutation({
    mutationFn: () => assetService.generateBarcode(id!),
    onSuccess: (a) => {
      message.success('Barcode generated');
      form.setFieldValue('barcode', a.barcode);
      void queryClient.invalidateQueries({ queryKey: ['asset', id] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed to generate barcode'),
  });
  const genQrMut = useMutation({
    mutationFn: () => assetService.generateQr(id!),
    onSuccess: (a) => {
      message.success('QR link set');
      form.setFieldValue('qr_code', a.qr_code);
      void queryClient.invalidateQueries({ queryKey: ['asset', id] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed to generate QR'),
  });

  const onFinish = (values: Record<string, unknown>) => {
    const body: Record<string, unknown> = {
      ...values,
      purchase_date: formatDate(values.purchase_date as dayjs.Dayjs | null),
      capitalization_date: formatDate(values.capitalization_date as dayjs.Dayjs | null),
      warranty_expiry: formatDate(values.warranty_expiry as dayjs.Dayjs | null),
    };
    if (isEdit && id) {
      updateMut.mutate({ i: id, body });
    } else {
      createMut.mutate(body);
    }
  };

  const whOpts = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: `${w.warehouse_code} — ${w.warehouse_name}` })),
    [warehouses]
  );
  const deptOpts = useMemo(
    () => departments.map((d) => ({ value: d.id, label: `${d.department_code} — ${d.department_name}` })),
    [departments]
  );
  const empOpts = useMemo(
    () => employees.map((e) => ({ value: e.id, label: `${e.employee_code || ''} ${e.full_name}`.trim() })),
    [employees]
  );
  const supOpts = useMemo(
    () => suppliers.map((s) => ({ value: s.id, label: `${s.supplier_code} — ${s.supplier_name}` })),
    [suppliers]
  );

  const movColumns: ColumnsType<AssetMovementDto> = useMemo(
    () => [
      {
        title: 'Type',
        dataIndex: 'movement_type',
        key: 't',
        width: 100,
        render: (t: string) => (
          <Tag>{t === 'issue' ? 'Issue' : t === 'return' ? 'Return' : 'Transfer'}</Tag>
        ),
      },
      {
        title: 'When',
        dataIndex: 'created_at',
        key: 'c',
        width: 180,
        render: (d: string) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '—'),
      },
      {
        title: 'To',
        key: 'to',
        ellipsis: true,
        render: (_: unknown, m: AssetMovementDto) => {
          const parts: string[] = [];
          if (m.to_employee_id) {
            const e = employees.find((x) => x.id === m.to_employee_id);
            parts.push(e ? e.full_name : 'Unknown employee');
          }
          if (m.to_warehouse_id) {
            const w = warehouses.find((x) => x.id === m.to_warehouse_id);
            parts.push(w ? w.warehouse_code : m.to_warehouse_id);
          }
          if (m.to_department_id) {
            const d0 = departments.find((x) => x.id === m.to_department_id);
            parts.push(d0 ? d0.department_code : m.to_department_id);
          }
          if (m.to_site) parts.push(m.to_site);
          if (m.to_building) parts.push(m.to_building);
          if (m.to_room) parts.push(m.to_room);
          return parts.length ? parts.join(' · ') : '—';
        },
      },
      { title: 'Remarks', dataIndex: 'remarks', key: 'r', ellipsis: true },
    ],
    [departments, employees, warehouses]
  );

  const movementBlocked = existing?.status === 'draft' || existing?.status === 'disposed';
  const showIssueMov = Boolean(canRecordMovement && !movementBlocked && existing?.status && existing.status !== 'in_use');
  const showReturnMov = Boolean(canRecordMovement && !movementBlocked && existing?.status === 'in_use');
  const showTransferMov = Boolean(canRecordMovement && !movementBlocked);


  if (isEdit && loadingRow) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
      {id ? <AiPageContextSync module="assets" docType="Asset" docId={id} /> : null}
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {id ? <AiPageContextSync module="assets" docType="Asset" docId={id} /> : null}
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/assets')}>
          Back
        </Button>
        {isEdit && id && (
          <Button type="link" onClick={() => navigate(`/asset/assets/${id}`)}>
            Asset view
          </Button>
        )}
        {isEdit && id && (
          <Button type="link" onClick={() => void navigate(`/asset/maintenance?asset=${id}`)}>
            Maintenance
          </Button>
        )}
      </Space>
      {isEdit && id && (
        <AssetFleetMembershipCard
          assetId={id}
          assetCode={existing?.asset_code}
          assetName={existing?.asset_name}
          disposed={existing?.status === 'disposed'}
          canManagePmv={canManagePmv}
          loading={pmvProfileLoading}
          profile={pmvProfile}
          profileLoaded={pmvProfileLoaded}
          projectLabel={
            (pmvWorkspace as { operational?: { allocation?: { state?: string; project_id?: string } } })?.operational
              ?.allocation?.state === 'ACTIVE'
              ? resolveProjectDisplayFromMap(
                  projectLabels,
                  (pmvWorkspace as { operational?: { allocation?: { project_id?: string } } }).operational?.allocation
                    ?.project_id
                )
              : null
          }
          expectedRelease={
            (pmvWorkspace as { operational?: { allocation?: { state?: string; planned_end?: string } } })?.operational
              ?.allocation?.state === 'ACTIVE'
              ? String(
                  (pmvWorkspace as { operational?: { allocation?: { planned_end?: string } } }).operational?.allocation
                    ?.planned_end ?? ''
                ) || null
              : null
          }
        />
      )}
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          {isEdit ? 'Edit asset' : 'New asset'}
        </Title>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: 'draft',
            depreciation_method: 'straight_line',
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="asset_code" label="Asset code">
                <Input placeholder="Leave blank to auto-generate" disabled={isEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="asset_name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'active', label: 'Active' },
                    { value: 'in_use', label: 'In use' },
                    { value: 'under_maintenance', label: 'Under maintenance' },
                    { value: 'idle', label: 'Idle' },
                    { value: 'disposed', label: 'Disposed' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="depreciation_method" label="Depreciation method">
                <Select
                  allowClear
                  options={[
                    { value: 'straight_line', label: 'Straight line' },
                    { value: 'wdv', label: 'Written down value' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="asset_group_id"
                label="Asset group"
                extra="Drives group-wise GL for depreciation / disposal / capitalize"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select group"
                  options={assetGroups.map((g) => ({
                    value: g.id,
                    label: `${g.code} — ${g.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="asset_category" label="Category">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="asset_subcategory" label="Subcategory">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="serial_number" label="Serial number">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="barcode" label="Barcode">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="qr_code" label="QR / link">
                <Input />
              </Form.Item>
            </Col>
            {isEdit && id && canEditAsset && (
              <Col span={24}>
                <Space wrap>
                  <Button
                    onClick={() => void genBarMut.mutateAsync()}
                    disabled={Boolean(existing?.barcode)}
                    loading={genBarMut.isPending}
                  >
                    Auto-generate barcode
                  </Button>
                  <Button onClick={() => void genQrMut.mutateAsync()} loading={genQrMut.isPending}>
                    Set QR to scan / lookup link
                  </Button>
                </Space>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  Configure FRONTEND_BASE_URL (or PUBLIC_APP_URL) on the API for full app URLs; otherwise a compact
                  withu-asset|… value is used.
                </Text>
              </Col>
            )}
            {isEdit && (qrForPreview || existing?.qr_code) && (
              <Col>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                      Preview
                    </Text>
                    <QRCodeSVG
                      value={(qrForPreview as string) || (existing?.qr_code as string) || ''}
                      size={120}
                    />
                  </div>
                </div>
              </Col>
            )}
            {isEdit && existing?.last_verified_at && (
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last physical scan verification: {dayjs(existing.last_verified_at).format('YYYY-MM-DD HH:mm')}
                </Text>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <Form.Item name="manufacturer" label="Manufacturer">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="model" label="Model">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="specification" label="Specification">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="is_measuring_equipment"
                label="Measuring equipment (Q1)"
                valuePropName="checked"
                initialValue={false}
                extra="Mark if used for product conformity evidence or process parameter monitoring (API Q1)."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="equipment_ownership"
                label="Equipment ownership"
                extra="For customer-, employee-, or third-party–owned instruments."
              >
                <Select
                  allowClear
                  placeholder="Select ownership"
                  options={[
                    { value: 'CompanyOwned', label: 'Company owned' },
                    { value: 'CustomerOwned', label: 'Customer owned' },
                    { value: 'EmployeeOwned', label: 'Employee owned' },
                    { value: 'ThirdParty', label: 'Third party (e.g. on loan / external lab)' },
                  ]}
                />
              </Form.Item>
            </Col>
            {isMeasuring && (
              <Col xs={24} sm={12}>
                <Form.Item
                  name="identification_method"
                  label="Calibration identification"
                  extra="How the calibration status is shown on the instrument."
                >
                  <Select allowClear placeholder="Select method" options={IDENTIFICATION_METHOD_OPTIONS} />
                </Form.Item>
              </Col>
            )}
            {isEdit && id && existing?.is_measuring_equipment && (
              <Col xs={24} sm={12} style={{ display: 'flex', alignItems: 'center' }}>
                <Space>
                  {existing.calibration_status && (
                    <Tag color={tmmdeStatusColor(existing.calibration_status)}>
                      {tmmdeStatusLabel(existing.calibration_status)}
                    </Tag>
                  )}
                  <Button onClick={() => navigate(`/qms/tmmde/${id}`)}>Calibration &amp; status</Button>
                </Space>
              </Col>
            )}
            <Col xs={24} sm={8}>
              <Form.Item name="purchase_date" label="Purchase date">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="capitalization_date" label="Capitalization date">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="warranty_expiry" label="Warranty expiry">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="purchase_value" label="Purchase value">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="residual_value" label="Residual value">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="current_book_value" label="Current book value">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="useful_life_months" label="Useful life (months)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="warehouse_id" label="Warehouse">
                <Select allowClear showSearch optionFilterProp="label" options={whOpts} placeholder="Select warehouse" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="site" label="Site">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="building" label="Building">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="room" label="Room">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="employee_id" label="Assigned to (employee)">
                <Select allowClear showSearch optionFilterProp="label" options={empOpts} placeholder="Select employee" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="department_id" label="Department">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={deptOpts}
                  placeholder="Select department"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="supplier_id" label="Vendor (supplier)">
                <Select allowClear showSearch optionFilterProp="label" options={supOpts} placeholder="Select supplier" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remarks" label="Remarks">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={createMut.isPending || updateMut.isPending}
            >
              Save
            </Button>
            <Button onClick={() => navigate('/asset/assets')}>Cancel</Button>
          </Space>
        </Form>
      </Card>

      {isEdit && (
        <Card style={{ marginTop: 16 }}>
          <Title level={5} style={{ marginTop: 0 }}>
            Movements and custody
          </Title>
          {movementBlocked && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Movements are not available for draft or disposed assets.
            </Text>
          )}
          {!canRecordMovement && !movementBlocked && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              You need the asset.move permission to record issue, return, or transfer.
            </Text>
          )}
          {canRecordMovement && !movementBlocked && (
            <Space style={{ marginBottom: 12 }} wrap>
              {showIssueMov && (
                <Button type="primary" onClick={() => navigate(`/asset/assets/${id}/movements/issue`)}>
                  Issue to user
                </Button>
              )}
              {showReturnMov && (
                <Button onClick={() => navigate(`/asset/assets/${id}/movements/return`)}>Return from user</Button>
              )}
              {showTransferMov && (
                <Button onClick={() => navigate(`/asset/assets/${id}/movements/transfer`)}>Transfer</Button>
              )}
              <Button onClick={() => navigate(`/asset/assets/${id}/movements/damage`)}>Record damage</Button>
            </Space>
          )}
          <Table<AssetMovementDto>
            rowKey="id"
            size="small"
            dataSource={movements}
            columns={movColumns}
            pagination={false}
          />
        </Card>
      )}

      {isEdit && id ? <RelatedMocsPanel assetId={id} /> : null}

    </div>
  );
};

export default AssetFormPage;
